/**
 * The sections of a contact's profile, shared by the side panel and the full
 * window so the two surfaces cannot disagree about a person.
 *
 * Every section reads a `CrmContact` straight from the service and renders
 * what the record carries: an absent value reads "Not on record", never a
 * default, and nothing is synthesised on the way. The old profile fabricated
 * news headlines from the contact's own name, keyed "recurring counterparties"
 * to two sample ids, and rendered every handle as inert text; none of that
 * survives here. An address is a message waiting to be written, a deal id is a
 * dossier, a message subject is the message.
 */
import { Fragment, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import clsx from 'clsx'
import {
  Activity,
  AlertTriangle,
  ArrowRightLeft,
  Award,
  Ban,
  BarChart2,
  Briefcase,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Combine,
  FileEdit,
  GitMerge,
  GraduationCap,
  History,
  ListChecks,
  Loader2,
  Mail,
  MapPin,
  MessageSquare,
  Newspaper,
  Pin,
  RefreshCw,
  RotateCcw,
  Search,
  Sparkles,
  Star,
  StickyNote,
  TrendingUp,
  UserMinus,
  UserPlus,
  Users,
  Wand2,
} from 'lucide-react'

import {
  crmErrorDetail,
  crmService,
  partyTypeFor,
  type CrmActivity,
  type CrmActivityKind,
  type CrmContact,
  type CrmEnrichmentHistory,
  type CrmNewsArticle,
  type CrmNewsLane,
} from '@/services/crm'
import { formatUsd } from '@/utils/money'
import { externalHref, linkLabel, mailtoHref, mapsHref, openDeal, telHref } from './contactLinks'
import {
  NOT_ON_RECORD,
  dealRowsFor,
  distinctCommunications,
  editedByActor,
  enrichResultNote,
  formatDate,
  formatStamp,
  kindLabel,
  labelForField,
  labelledPhones,
  observedLabel,
  provenanceFor,
  provenanceTitle,
  relativeTime,
} from './crmFields'
import EmailViewer from './EmailViewer'
import { addressGroups, locationText } from './profileFacts'

// ── Shared chrome ──────────────────────────────────────────────────────────

/** A section frame: kicker title with a glyph, then the body. */
export function ProfileSection({
  title,
  icon,
  action,
  children,
  className,
}: {
  title: string
  icon?: ReactNode
  /** A control on the title row — a toggle, a small button. */
  action?: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <section className={clsx('border-b border-amber-400/10 px-4 py-3', className)}>
      {/* Wraps rather than overflows: in a narrow side panel the title row's
          control (e.g. Enriched data's History) ran past the panel's edge. */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
        <div className="flex min-w-0 items-center gap-1.5">
          {icon}
          <span className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-stone-500">{title}</span>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </section>
  )
}

const LINK_CLASS =
  'rounded text-stone-200 underline decoration-dotted decoration-stone-600 underline-offset-2 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60'

const CHIP_CLASS =
  'rounded border border-amber-400/10 bg-amber-400/[0.04] px-1.5 py-0.5 text-[9px] text-amber-300/70'

const ABSENT = <span className="italic text-stone-600">{NOT_ON_RECORD}</span>

/**
 * Where a value came from, as a tooltip glyph.
 *
 * Only drawn when something on record says — a hand edit, a lookup source, a
 * message the value was read from. A value with no recorded provenance gets
 * no badge rather than a guessed one.
 */
export function ProvenanceBadge({ contact, field }: { contact: CrmContact; field: string }) {
  const p = provenanceFor(contact, field)
  if (!p) return null
  const edited = /^Edited by|^Set by hand/.test(p.source)
  return (
    <span
      title={provenanceTitle(contact, field)}
      aria-label={provenanceTitle(contact, field)}
      className={clsx('ml-1 inline-flex align-middle', edited ? 'text-amber-400/80' : 'text-stone-600')}
    >
      {edited ? <Pin size={9} /> : <Search size={9} />}
    </span>
  )
}

/** One label/value row: mono kicker label, 12px value, provenance tooltip. */
export function ValueRow({
  contact,
  field,
  label,
  children,
  dense,
}: {
  contact: CrmContact
  field: string
  label: string
  children: ReactNode
  dense?: boolean
}) {
  return (
    <Fragment>
      <dt title={label} className={clsx('truncate font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500', dense ? 'pt-0.5' : 'pt-[3px]')}>
        {label}
      </dt>
      <dd className="min-w-0 break-words text-[12px] text-stone-200" title={provenanceTitle(contact, field)}>
        {children}
        <ProvenanceBadge contact={contact} field={field} />
      </dd>
    </Fragment>
  )
}

function RowGrid({ dense, children }: { dense?: boolean; children: ReactNode }) {
  return (
    <dl
      className={clsx(
        'grid gap-x-3',
        dense ? 'grid-cols-[76px_minmax(0,1fr)] gap-y-1' : 'grid-cols-[104px_minmax(0,1fr)] gap-y-1.5',
      )}
    >
      {children}
    </dl>
  )
}

function ExternalAnchor({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
      {label}
    </a>
  )
}

// ── Reach ──────────────────────────────────────────────────────────────────

/**
 * How to reach the contact: every address a mailto, every number a tel, the
 * address a map search, LinkedIn and the website in a new tab.
 */
export function ReachSection({ contact, dense }: { contact: CrmContact; dense?: boolean }) {
  // Real mailboxes only: Exchange routing identifiers are not a way to reach
  // anyone (the server names them in `routingAddresses`). They are counted,
  // not listed; the full contact shows them behind a toggle.
  const groups = addressGroups(contact)
  const primaryEmail = groups.primary
  const primaryHref = mailtoHref(primaryEmail)
  const others = groups.alternates.map((alt) => alt.address)
  const phones = labelledPhones(contact)
  const address = contact.address ?? locationText(contact)
  const addressHref = mapsHref(contact.address ?? contact.location ?? null)
  const linkedIn = externalHref(contact.enrichment?.linkedIn)
  const website = externalHref(contact.enrichment?.website)
  const captured = contact.source === 'Deal Capture'
  const tags = Array.from(new Set([...(contact.tags ?? []), ...(contact.capacities ?? [])]))

  return (
    <ProfileSection title="Reach" icon={<Mail size={11} className="text-amber-400" />}>
      <RowGrid dense={dense}>
        <ValueRow contact={contact} field="email" label="Email" dense={dense}>
          {primaryEmail ? (
            primaryHref ? (
              <a href={primaryHref} className={LINK_CLASS}>
                {primaryEmail}
              </a>
            ) : (
              primaryEmail
            )
          ) : groups.routing.length > 0 ? (
            <span className="italic text-stone-600">No mailbox on record</span>
          ) : (
            ABSENT
          )}
        </ValueRow>
        {others.length > 0 ? (
          <ValueRow contact={contact} field="emails" label="Also reachable at" dense={dense}>
            <span className="flex flex-wrap gap-1">
              {others.map((address) => {
                const href = mailtoHref(address)
                return href ? (
                  <a key={address} href={href} className={clsx(CHIP_CLASS, 'hover:text-amber-200')}>
                    {address}
                  </a>
                ) : (
                  <span key={address} className={CHIP_CLASS}>
                    {address}
                  </span>
                )
              })}
            </span>
          </ValueRow>
        ) : null}
        {groups.routing.length > 0 ? (
          <ValueRow contact={contact} field="emails" label="Routing IDs" dense={dense}>
            <span className="text-stone-500" title="Exchange routing identifiers. Nobody can write to them.">
              {groups.routing.length} system routing ID{groups.routing.length === 1 ? '' : 's'}, not mailboxes
            </span>
          </ValueRow>
        ) : null}
        {phones.map((row) => {
          const href = telHref(row.value)
          return (
            <ValueRow key={row.field} contact={contact} field={row.field} label={row.label || 'Phone'} dense={dense}>
              {row.value ? (
                href ? (
                  <a href={href} className={LINK_CLASS}>
                    {row.value}
                  </a>
                ) : (
                  row.value
                )
              ) : (
                ABSENT
              )}
            </ValueRow>
          )
        })}
        <ValueRow contact={contact} field="address" label="Address" dense={dense}>
          {address ? (
            addressHref ? (
              <a href={addressHref} target="_blank" rel="noopener noreferrer" className={LINK_CLASS}>
                <MapPin size={9} className="mr-1 inline align-[-1px] text-stone-500" />
                {address}
              </a>
            ) : (
              address
            )
          ) : (
            ABSENT
          )}
        </ValueRow>
        <ValueRow contact={contact} field="linkedIn" label="LinkedIn" dense={dense}>
          {linkedIn ? (
            <ExternalAnchor href={linkedIn} label={linkLabel(contact.enrichment.linkedIn)} />
          ) : contact.enrichment?.linkedIn ? (
            contact.enrichment.linkedIn
          ) : (
            ABSENT
          )}
        </ValueRow>
        <ValueRow contact={contact} field="website" label="Website" dense={dense}>
          {website ? (
            <ExternalAnchor href={website} label={linkLabel(contact.enrichment.website)} />
          ) : contact.enrichment?.website ? (
            contact.enrichment.website
          ) : (
            ABSENT
          )}
        </ValueRow>
      </RowGrid>

      {captured ? (
        // Which rule filed this contact where it is, so "why is this person a
        // broker" has an answer a reader can check.
        <div className="mt-2 text-[9px] text-stone-600">
          Captured from the deals · {contact.roleEvidence ?? 'Type not established by any record on file'}
        </div>
      ) : null}
      {(contact.editedFields ?? []).length > 0 ? (
        <div className="mt-1 flex items-start gap-1 text-[9px] text-stone-500">
          <Pin size={9} className="mt-0.5 shrink-0 text-amber-400/80" />
          <span>
            Edited by {contact.editedBy ?? 'a person'} · {contact.editedFields.map(labelForField).join(', ')}. Capture leaves
            these alone.
          </span>
        </div>
      ) : null}
      {tags.length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {tags.map((t) => (
            <span key={t} className={CHIP_CLASS}>
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </ProfileSection>
  )
}

/**
 * Who the contact is on the books: company, title, type and why, kind,
 * status, source, first and last seen. The window's first column pairs this
 * with `ReachSection`; the panel shows most of it in its header instead.
 */
export function IdentitySection({
  contact,
  dense,
  summarised = false,
}: {
  contact: CrmContact
  dense?: boolean
  /** The window's vitals strip states status, source, first and last seen;
   *  drawing them here as well is what crowded the column. */
  summarised?: boolean
}) {
  const entityTitle = partyTypeFor(contact.id) === 'sponsor'
  return (
    <ProfileSection title="Identity" icon={<Briefcase size={11} className="text-amber-400" />}>
      <RowGrid dense={dense}>
        <ValueRow contact={contact} field="company" label="Company" dense={dense}>
          {contact.company ?? ABSENT}
          {contact.companyDomain ? <span className="ml-1.5 font-mono text-[10px] text-stone-500">@{contact.companyDomain}</span> : null}
        </ValueRow>
        <ValueRow contact={contact} field={entityTitle ? 'entityType' : 'title'} label={entityTitle ? 'Entity type' : 'Title'} dense={dense}>
          {(entityTitle ? contact.entityType : contact.title) ?? ABSENT}
        </ValueRow>
        <ValueRow contact={contact} field="companyType" label="Type" dense={dense}>
          {contact.companyType}
          {contact.roleEvidence ? <span className="ml-1.5 text-[10px] text-stone-500">— {contact.roleEvidence}</span> : null}
        </ValueRow>
        <ValueRow contact={contact} field="kind" label="Kind" dense={dense}>
          {kindLabel(contact.kind, contact.isEntity)}
        </ValueRow>
        <ValueRow contact={contact} field="isInternal" label="Internal" dense={dense}>
          {contact.isInternal ? 'Yes — the tenant’s own staff' : 'No'}
        </ValueRow>
        {summarised ? null : (
          <>
            <ValueRow contact={contact} field="status" label="Status" dense={dense}>
              {contact.status || ABSENT}
            </ValueRow>
            <ValueRow contact={contact} field="source" label="Source" dense={dense}>
              {contact.source || ABSENT}
            </ValueRow>
            <ValueRow contact={contact} field="firstSeen" label="First seen" dense={dense}>
              {contact.firstSeen ? formatDate(contact.firstSeen) : ABSENT}
            </ValueRow>
            <ValueRow contact={contact} field="lastContact" label="Last seen" dense={dense}>
              {contact.lastContact ? (
                <span title={formatStamp(contact.lastContact)}>{formatDate(contact.lastContact)}</span>
              ) : (
                ABSENT
              )}
            </ValueRow>
          </>
        )}
        {(contact.emailDomains ?? []).length > 0 ? (
          <ValueRow contact={contact} field="emailDomains" label="Email domains" dense={dense}>
            <span className="flex flex-wrap gap-1">
              {contact.emailDomains!.map((d) => (
                <span key={d} className={clsx(CHIP_CLASS, 'font-mono')}>
                  @{d}
                </span>
              ))}
            </span>
          </ValueRow>
        ) : null}
      </RowGrid>
    </ProfileSection>
  )
}

// ── Deals ──────────────────────────────────────────────────────────────────

function activateOnKey(handler: () => void) {
  return (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      handler()
    }
  }
}

export function DealsSection({ contact, layout = 'table' }: { contact: CrmContact; layout?: 'table' | 'list' }) {
  const rows = dealRowsFor(contact)
  return (
    <ProfileSection title="Deals" icon={<ChevronRight size={11} className="text-amber-400" />}>
      {rows.length === 0 ? (
        <div className="text-[11px] italic text-stone-500">No linked deals</div>
      ) : layout === 'list' ? (
        // A six-column table in a quarter-width column wrapped every deal id
        // over three lines. Two lines per deal carry the same six values.
        <ul className="space-y-1">
          {rows.map((d) => {
            const open = () => openDeal(d.dealId)
            const facts = [d.stage, d.role, d.date ? formatDate(d.date) : null].filter(Boolean).join(' · ')
            return (
              <li
                key={d.dealId}
                tabIndex={0}
                onClick={open}
                onKeyDown={activateOnKey(open)}
                title="Open the deal"
                className="cursor-pointer rounded border border-amber-400/[0.06] bg-black/20 px-2 py-1.5 hover:border-amber-400/20 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 truncate text-[11px] font-medium text-stone-200">{d.property ?? ABSENT}</span>
                  <span className="shrink-0 font-mono text-[11px] text-stone-200">{d.amount == null ? ABSENT : formatUsd(d.amount)}</span>
                </div>
                <div className="mt-0.5 flex items-baseline justify-between gap-2 text-[9px]">
                  <span className="min-w-0 truncate font-mono text-amber-300/80">{d.dealId}</span>
                  <span className="min-w-0 truncate text-right text-stone-400" title={facts || undefined}>
                    {facts || ABSENT}
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="dense-table">
            <thead>
              <tr>
                <th>Deal</th>
                <th>Property</th>
                <th>Amount</th>
                <th>Stage</th>
                <th>Role</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => {
                const open = () => openDeal(d.dealId)
                return (
                  <tr
                    key={d.dealId}
                    tabIndex={0}
                    onClick={open}
                    onKeyDown={activateOnKey(open)}
                    title="Open the deal"
                    className="cursor-pointer focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                  >
                    <td className="font-mono text-[10px] text-amber-300/80">{d.dealId}</td>
                    <td>{d.property ?? ABSENT}</td>
                    <td className="font-mono text-[11px]">{d.amount == null ? ABSENT : formatUsd(d.amount)}</td>
                    <td>{d.stage ?? ABSENT}</td>
                    <td>{d.role ?? ABSENT}</td>
                    <td className="font-mono text-[10px] text-stone-400">{d.date ? formatDate(d.date) : ABSENT}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </ProfileSection>
  )
}

// ── Communications ─────────────────────────────────────────────────────────

export function CommunicationsSection({ contact }: { contact: CrmContact }) {
  const [openId, setOpenId] = useState<string | null>(null)
  const comms = distinctCommunications(contact.communications ?? [])
  return (
    <ProfileSection title="Communications" icon={<Mail size={11} className="text-amber-400" />}>
      {comms.length === 0 ? (
        <div className="text-[11px] italic text-stone-500">No communications logged</div>
      ) : (
        <ul className="space-y-1">
          {comms.map((comm) => {
            const ingestionId = comm.ingestionId ?? null
            const open = ingestionId ? () => setOpenId(ingestionId) : null
            const subject = comm.subject ?? comm.type
            const preview = comm.preview && !comm.capacities.includes(comm.preview) ? comm.preview : null
            const hasMeta = Boolean(comm.direction || comm.capacities.length > 0 || comm.dealId)
            return (
              <li
                key={comm.id}
                className={clsx(
                  'rounded border border-amber-400/[0.06] bg-black/20 px-2 py-1.5',
                  open && 'cursor-pointer hover:border-amber-400/20 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60',
                )}
                tabIndex={open ? 0 : undefined}
                onClick={open ?? undefined}
                onKeyDown={open ? activateOnKey(open) : undefined}
                title={open ? 'Open the stored message' : 'No stored message: this entry was logged by hand, not read from the mail'}
              >
                {/* Subject and time on the first line, who and which deal on
                    the second: the subject is what a reader scans for. */}
                <div className="flex items-center gap-1.5">
                  {/* An unrecorded direction is neutral: colouring it as
                      outbound would assert something nobody logged. */}
                  <Mail
                    size={11}
                    className={clsx(
                      'shrink-0',
                      comm.direction === 'Inbound' ? 'text-blue-400' : comm.direction === 'Outbound' ? 'text-emerald-400' : 'text-stone-500',
                    )}
                  />
                  <span className="min-w-0 flex-1 truncate text-[11px] font-medium text-stone-200">{subject}</span>
                  <span className="shrink-0 font-mono text-[9px] text-stone-600" title={formatStamp(comm.timestamp)}>
                    {relativeTime(comm.timestamp)}
                  </span>
                </div>
                {preview ? <div className="mt-0.5 truncate pl-[17px] text-[10px] text-stone-500">{preview}</div> : null}
                {hasMeta ? (
                  <div className="mt-1 flex min-w-0 items-center gap-1 pl-[17px]">
                    {comm.direction ? (
                      <span className={clsx('text-[9px] font-medium', comm.direction === 'Inbound' ? 'text-blue-300' : 'text-emerald-300')}>
                        {comm.direction}
                      </span>
                    ) : null}
                    {comm.capacities.map((capacity) => (
                      <span key={capacity} className={CHIP_CLASS}>
                        {capacity}
                      </span>
                    ))}
                    {comm.dealId ? (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation()
                          openDeal(comm.dealId!)
                        }}
                        className="ml-auto min-w-0 truncate rounded font-mono text-[9px] text-amber-300/80 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                        title="Open the deal"
                      >
                        {comm.dealId}
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
      {openId ? <EmailViewer ingestionId={openId} onClose={() => setOpenId(null)} /> : null}
    </ProfileSection>
  )
}

// ── Enrichment ─────────────────────────────────────────────────────────────

/**
 * What external lookup knows, with a Re-enrich whose result stays with THIS
 * contact, and a history of every observation newest first.
 *
 * The old page kept one page-wide result string, so selecting another row
 * showed the previous contact's "Updated 3 fields." under the new one.
 */
export function EnrichmentSection({ contact, showHistory = false }: { contact: CrmContact; showHistory?: boolean }) {
  const queryClient = useQueryClient()
  const [note, setNote] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(showHistory)
  const e = contact.enrichment
  const partyType = partyTypeFor(contact.id)

  const enrich = useMutation({
    mutationFn: () => crmService.enrichContact(contact.id),
    onSuccess: (result) => {
      setNote(enrichResultNote(result))
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'enrichment'] })
    },
    onError: (err) => setNote(crmErrorDetail(err, 'The lookup could not be run.')),
  })

  const history = useQuery({
    queryKey: ['crm', 'enrichment', partyType, contact.id],
    queryFn: () => crmService.getEnrichmentHistory(partyType, contact.id),
    enabled: historyOpen,
  })

  const sources = e?.sources ?? []
  const answered = sources.filter((s) => s.ok).map((s) => s.name)
  const failed = sources.filter((s) => !s.ok && s.detail)
  const extra = e?.extra ?? []

  return (
    <ProfileSection
      title="Enriched data"
      icon={<Award size={11} className="text-amber-400" />}
      action={
        <button
          type="button"
          onClick={() => setHistoryOpen((v) => !v)}
          aria-expanded={historyOpen}
          className="flex items-center gap-1 rounded font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
        >
          <History size={9} />
          History
          {historyOpen ? <ChevronUp size={9} /> : <ChevronDown size={9} />}
        </button>
      }
    >
      {/* The difference between "we looked and found nothing" and "we have
          not looked" is the whole value of this panel. */}
      <div className="mb-1.5 text-[9px] text-stone-600">
        {e?.lookedUpAt ? `External lookup last run ${relativeTime(e.lookedUpAt)}` : 'External lookup has not run for this party.'}
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
        <div>
          <span className="text-stone-500">Experience</span>
          <span className="ml-1.5 text-stone-200">{e?.yearsExperience == null ? NOT_ON_RECORD : `${e.yearsExperience} years`}</span>
        </div>
        <div>
          <span className="text-stone-500">Deal volume</span>
          <span className="ml-1.5 text-stone-200">{e?.dealVolume ?? NOT_ON_RECORD}</span>
        </div>
        {e?.education ? (
          <div className="col-span-2 flex items-center gap-1">
            <GraduationCap size={10} className="text-stone-500" />
            <span className="text-stone-300">{e.education}</span>
          </div>
        ) : null}
        {(e?.licenses ?? []).length > 0 ? (
          <div className="col-span-2">
            <span className="text-stone-500">Licenses:</span>
            <span className="ml-1.5 text-stone-300">{e.licenses.join(', ')}</span>
          </div>
        ) : null}
        {(e?.previousCompanies ?? []).length > 0 ? (
          <div className="col-span-2 flex items-start gap-1">
            <Briefcase size={10} className="mt-0.5 text-stone-500" />
            <span className="text-stone-300">{e.previousCompanies.join(' > ')}</span>
          </div>
        ) : null}
      </div>
      {(e?.specialties ?? []).length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {e.specialties.map((s) => (
            <span key={s} className={CHIP_CLASS}>
              {s}
            </span>
          ))}
        </div>
      ) : null}
      {extra.length > 0 ? (
        <div className="mt-2 border-t border-amber-400/[0.07] pt-1.5">
          <div className="mb-1 font-mono text-[8px] uppercase tracking-widest text-stone-600">Also observed</div>
          <dl className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-2 gap-y-0.5 text-[10px]">
            {extra.map((row, i) => (
              <Fragment key={`${row.field}-${i}`}>
                <dt className="truncate text-stone-500" title={observedLabel(row.field)}>
                  {observedLabel(row.field)}
                </dt>
                <dd className="min-w-0 break-words text-stone-300">
                  {typeof row.value === 'object' && row.value !== null ? JSON.stringify(row.value) : String(row.value)}
                  {row.source ? <span className="ml-1.5 text-[8px] text-stone-600">· {row.source}</span> : null}
                </dd>
              </Fragment>
            ))}
          </dl>
        </div>
      ) : null}
      <div className="mt-2 flex items-center justify-between gap-2">
        {/* Named the sources it would consult as though it had. The honest
            line is which sources actually answered. */}
        <span className="min-w-0 text-[9px] italic text-stone-600">
          {sources.length === 0
            ? 'No external source has been consulted for this party.'
            : answered.length > 0
              ? `Sourced from ${answered.join(', ')}`
              : 'A lookup ran, but no source answered — see below.'}
        </span>
        <button
          type="button"
          disabled={enrich.isPending}
          onClick={() => enrich.mutate()}
          title="Look this party up externally"
          className="flex shrink-0 items-center gap-1 rounded text-[9px] text-amber-400 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:text-stone-600"
        >
          <RefreshCw size={9} className={enrich.isPending ? 'motion-safe:animate-spin' : undefined} />
          {enrich.isPending ? 'Looking up…' : 'Re-enrich'}
        </button>
      </div>
      {note ? <div className="mt-1 text-[9px] text-stone-500">{note}</div> : null}
      {failed.length > 0 ? (
        <ul className="mt-1 space-y-0.5">
          {failed.map((s) => (
            <li key={s.name} className="text-[9px] leading-tight text-stone-600">
              <span className="text-stone-500">{s.name}:</span> {s.detail}
            </li>
          ))}
        </ul>
      ) : null}
      {historyOpen ? <EnrichmentHistory contact={contact} state={history} /> : null}
    </ProfileSection>
  )
}

function newsLaneTitle(lane: CrmNewsLane): string {
  switch (lane) {
    case 'person':
      return 'About them'
    case 'company':
      return 'About their firm'
    default: {
      const exhaustive: never = lane
      return exhaustive
    }
  }
}

function newsPublishedLabel(value: string | null): string | null {
  if (!value) return null
  return /^\d{4}-\d{2}-\d{2}/.test(value) ? formatDate(value) : value
}

function NewsArticleList({ articles }: { articles: CrmNewsArticle[] }) {
  if (articles.length === 0) {
    return <div className="text-[11px] italic text-stone-600">No matching articles</div>
  }
  return (
    <ul className="space-y-1.5">
      {articles.map((article) => {
        const href = externalHref(article.url)
        const when = newsPublishedLabel(article.publishedAt)
        return (
          <li key={`${article.lane}-${article.url}`} className="min-w-0">
            {href ? (
              <a href={href} target="_blank" rel="noopener noreferrer" className={LINK_CLASS} title={article.query}>
                {article.title}
              </a>
            ) : (
              <span className="text-[12px] text-stone-200">{article.title}</span>
            )}
            <div className="mt-0.5 text-[9px] text-stone-600">
              {[article.publisher, when].filter(Boolean).join(' · ') || 'Publisher not recorded'}
            </div>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * Recent news about the person and their firm, from the last Re-enrich.
 *
 * Three honest states, never a demo headline: the News source is unconfigured,
 * a lookup ran and kept nothing, or the stored articles are shown by lane.
 * Opening the profile does not search — Re-enrich does.
 */
export function NewsSection({ contact }: { contact: CrmContact }) {
  const e = contact.enrichment
  const articles = e?.news ?? []
  const newsSource = (e?.sources ?? []).find((s) => s.name === 'News')
  const person = articles.filter((a) => a.lane === 'person')
  const company = articles.filter((a) => a.lane === 'company')
  const hasArticles = articles.length > 0
  const unconfigured = Boolean(newsSource && !newsSource.ok)
  const lookedEmpty = Boolean(newsSource?.ok && !hasArticles)
  // Never looked up: whether Re-enrich would search news at all depends on
  // the client's sign-off switch, which the CRM summary carries.
  const neverLookedUp = !hasArticles && !newsSource
  const summary = useQuery({
    queryKey: ['crm', 'enrichment', 'summary'],
    queryFn: () => crmService.getEnrichmentSummary(),
    enabled: neverLookedUp,
  })
  const newsSearchApproved = summary.data?.newsSearchApproved

  let body: ReactNode
  if (hasArticles) {
    body = (
      <div className="space-y-3">
        <div>
          <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-600">{newsLaneTitle('person')}</div>
          <NewsArticleList articles={person} />
        </div>
        <div>
          <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-600">{newsLaneTitle('company')}</div>
          <NewsArticleList articles={company} />
        </div>
      </div>
    )
  } else if (unconfigured) {
    body = (
      <div className="text-[11px] text-stone-500">
        {newsSource?.detail || 'News is not configured, so nothing was looked up.'}
      </div>
    )
  } else if (lookedEmpty) {
    body = (
      <div className="text-[11px] italic text-stone-500">
        {newsSource?.detail || 'Looked up; no matching articles about them or their firm.'}
      </div>
    )
  } else {
    body = (
      <div className="text-[11px] italic text-stone-500">
        News has not been looked up for this party.
        {newsSearchApproved === false
          ? ' News search is off until the client’s written sign-off is recorded, so Re-enrich does not search it.'
          : newsSearchApproved === true
            ? ' Re-enrich to search.'
            : ''}
      </div>
    )
  }

  return (
    <ProfileSection title="News" icon={<Newspaper size={11} className="text-amber-400" />}>
      {body}
    </ProfileSection>
  )
}

export function EnrichmentHistory({ contact, state }: { contact: CrmContact; state: UseQueryResult<CrmEnrichmentHistory> }) {
  if (state.isPending) {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-[9px] text-stone-500">
        <Loader2 size={9} className="motion-safe:animate-spin" /> Reading the trail…
      </div>
    )
  }
  if (state.isError) {
    return <div className="mt-2 text-[9px] text-red-300">{crmErrorDetail(state.error, 'The enrichment history could not be read.')}</div>
  }
  const rows = state.data.observations ?? []
  return (
    <div className="mt-2 border-t border-amber-400/[0.07] pt-1.5">
      <div className="mb-1 flex items-center justify-between font-mono text-[8px] uppercase tracking-widest text-stone-600">
        <span>Observations, newest first</span>
        <span>{state.data.lookupCount} lookup{state.data.lookupCount === 1 ? '' : 's'}</span>
      </div>
      {rows.length === 0 ? (
        <div className="text-[10px] italic text-stone-600">Nothing has been observed for {contact.displayName} yet.</div>
      ) : (
        <ol className="space-y-1">
          {rows.map((row, i) => {
            const actor = row.pinned ? editedByActor(row.source) : null
            return (
              <li key={`${row.field}-${row.observedAt ?? i}`} className="grid grid-cols-[96px_minmax(0,1fr)] gap-x-2 text-[10px]">
                <span className="truncate text-stone-500" title={labelForField(row.field)}>
                  {labelForField(row.field)}
                </span>
                <span className="min-w-0 break-words text-stone-300">
                  {row.field === 'news' && Array.isArray(row.value) ? (
                    `${row.value.length} article${row.value.length === 1 ? '' : 's'}`
                  ) : row.value === null || row.value === undefined || row.value === '' ? (
                    <span className="italic text-stone-600">cleared</span>
                  ) : (
                    String(row.value)
                  )}
                  <span className="ml-1.5 text-[8px] text-stone-600">
                    {row.pinned ? (
                      <span className="text-amber-400/80">
                        <Pin size={8} className="mr-0.5 inline align-[-1px]" />
                        set by {actor ?? 'a person'}
                      </span>
                    ) : (
                      row.source ?? 'no source on file (suggestion)'
                    )}
                    {row.observedAt ? ` · ${formatStamp(row.observedAt)}` : ''}
                  </span>
                </span>
              </li>
            )
          })}
        </ol>
      )}
    </div>
  )
}

// ── Broker intelligence ────────────────────────────────────────────────────

export function BrokerIntelligenceSection({ contact }: { contact: CrmContact }) {
  const m = contact.metrics ?? {}
  const stalking = m.stalkingHorseCount ?? 0
  const specialties = Array.from(new Set([...(contact.tags ?? []), ...(contact.enrichment?.specialties ?? [])]))
  const common = m.commonSponsors ?? []
  const metrics = [
    // Every one of these was once synthesised from the length of the
    // linked-deal array. The platform records all three; these read them.
    {
      label: 'Hit rate',
      value: m.hitRate == null ? '—' : `${Math.round(m.hitRate * 100)}%`,
      sub: 'funded / submitted',
      color: 'text-emerald-400',
      icon: <TrendingUp size={10} />,
    },
    { label: 'Submissions', value: m.totalSubmissions ?? '—', sub: 'all time', color: 'text-stone-200', icon: <Activity size={10} /> },
    { label: 'Funded', value: m.fundedDeals ?? '—', sub: 'closed with us', color: 'text-emerald-400', icon: <Star size={10} /> },
  ]
  const flags = [
    {
      label: 'Stalking-horse activity',
      value: stalking > 0 ? `${stalking}× recorded` : 'None recorded',
      flag: stalking > 0,
      preferred: false,
      icon: <Ban size={9} />,
    },
    {
      label: 'Repeat offender',
      value: m.isRepeatOffender ? 'Yes — shops deals broadly' : 'No',
      flag: Boolean(m.isRepeatOffender),
      preferred: false,
      icon: <AlertTriangle size={9} />,
    },
    {
      label: 'Preferred broker',
      value: m.isPreferred == null ? NOT_ON_RECORD : m.isPreferred ? 'Yes' : 'No',
      flag: false,
      preferred: Boolean(m.isPreferred),
      icon: <Star size={9} />,
    },
    {
      label: 'Blocklisted',
      value: m.isBlocklisted == null ? NOT_ON_RECORD : m.isBlocklisted ? 'Yes' : 'No',
      flag: Boolean(m.isBlocklisted),
      preferred: false,
      icon: <Ban size={9} />,
    },
    {
      label: 'Avg deal quality',
      value: contact.score === null ? NOT_ON_RECORD : `${(contact.score / 10).toFixed(1)}/10`,
      flag: contact.score !== null && contact.score < 70,
      preferred: false,
      icon: <BarChart2 size={9} />,
    },
  ]
  return (
    <ProfileSection title="Broker intelligence" icon={<BarChart2 size={11} className="text-amber-400" />}>
      <div className="mb-3 grid grid-cols-3 gap-2">
        {metrics.map((x) => (
          <div key={x.label} className="rounded-lg border border-amber-400/10 bg-black/20 px-2.5 py-2">
            <div className={clsx('mb-0.5 flex items-center gap-1 text-[10px]', x.color)}>
              {x.icon}
              <span className="font-medium">{x.value}</span>
            </div>
            <div className="text-[9px] text-stone-500">{x.label}</div>
            <div className="text-[8px] text-stone-700">{x.sub}</div>
          </div>
        ))}
      </div>
      <div className="mb-3 space-y-1.5">
        <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-600">Behavioral flags</div>
        {flags.map((f) => (
          <div key={f.label} className="flex items-center gap-2 text-[10px]">
            <span className={f.flag ? 'text-red-400' : f.preferred ? 'text-amber-400' : 'text-stone-600'}>{f.icon}</span>
            <span className="w-[140px] text-stone-500">{f.label}</span>
            <span className={f.flag ? 'text-red-400' : f.preferred ? 'text-amber-300' : 'text-stone-300'}>{f.value}</span>
          </div>
        ))}
      </div>
      <div className="mb-2">
        <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-600">Recurring counterparties</div>
        <div className="flex items-start gap-1.5 text-[10px]">
          <Users size={9} className="mt-0.5 text-stone-600" />
          {/* Was keyed to two sample ids, so every real broker read as having
              no pattern. The record carries the answer. */}
          {common.length > 0 ? (
            <span className="flex flex-wrap gap-1">
              {common.map((s) => (
                <span key={s} className={CHIP_CLASS}>
                  {s}
                </span>
              ))}
            </span>
          ) : (
            <span className="text-stone-500">No recurring pattern recorded</span>
          )}
        </div>
      </div>
      {specialties.length > 0 ? (
        <div>
          <div className="mb-1 font-mono text-[9px] uppercase tracking-widest text-stone-600">Markets &amp; specialties</div>
          <div className="flex flex-wrap gap-1">
            {specialties.map((t) => (
              <span key={t} className={CHIP_CLASS}>
                {t}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </ProfileSection>
  )
}

// ── Sponsor financials ─────────────────────────────────────────────────────

export function SponsorFinancialsSection({ contact }: { contact: CrmContact }) {
  const m = contact.metrics ?? {}
  const cells = [
    { label: 'Experience', value: m.yearsExperience == null ? NOT_ON_RECORD : `${m.yearsExperience} years` },
    { label: 'Net worth', value: m.netWorth == null ? NOT_ON_RECORD : formatUsd(m.netWorth) },
    { label: 'Liquidity', value: m.liquidity == null ? NOT_ON_RECORD : formatUsd(m.liquidity) },
  ]
  return (
    <ProfileSection title="Sponsor financials" icon={<TrendingUp size={11} className="text-amber-400" />}>
      <div className="grid grid-cols-3 gap-2">
        {cells.map((c) => (
          <div key={c.label} className="metric-cell">
            <div className="metric-cell-label">{c.label}</div>
            <div className={clsx('metric-cell-value !text-[13px]', c.value === NOT_ON_RECORD && 'italic !text-stone-600')}>{c.value}</div>
          </div>
        ))}
      </div>
      {(m.flags ?? []).length > 0 ? (
        <div className="mt-2 flex flex-wrap gap-1">
          {m.flags!.map((f) => (
            <span key={f} className="rounded border border-red-400/20 bg-red-400/[0.06] px-1.5 py-0.5 text-[9px] text-red-300">
              {f}
            </span>
          ))}
        </div>
      ) : (
        <div className="mt-2 text-[9px] text-stone-600">No flags recorded.</div>
      )}
    </ProfileSection>
  )
}

// ── Notes ──────────────────────────────────────────────────────────────────

export function NotesSection({ contact, inputRef }: { contact: CrmContact; inputRef?: Ref<HTMLInputElement> }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | null>(null)
  const add = useMutation({
    mutationFn: (text: string) => crmService.addNote(contact.id, text),
    onSuccess: () => {
      setDraft('')
      setError(null)
      queryClient.invalidateQueries({ queryKey: ['crm', 'contacts'] })
      queryClient.invalidateQueries({ queryKey: ['crm', 'activity'] })
    },
    onError: (err) => setError(crmErrorDetail(err, 'The note could not be saved.')),
  })
  const submit = () => {
    const text = draft.trim()
    if (!text || add.isPending) return
    add.mutate(text)
  }
  return (
    <ProfileSection title="Notes" icon={<StickyNote size={11} className="text-amber-400" />}>
      {contact.notes ? (
        <div className="mb-2 whitespace-pre-wrap rounded border border-amber-400/[0.06] bg-black/20 px-2 py-1.5 text-[11px] leading-relaxed text-stone-300">
          {contact.notes}
        </div>
      ) : (
        <div className="mb-2 text-[11px] italic text-stone-500">No notes on record</div>
      )}
      <form
        className="flex items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <input
          ref={inputRef}
          type="text"
          aria-label="Add a note"
          placeholder="Add a note…"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          className="input input-compact w-full"
        />
        <button
          type="submit"
          disabled={!draft.trim() || add.isPending}
          className="shrink-0 rounded-md border border-amber-400/20 bg-amber-400/[0.06] px-2 py-1 text-[10px] font-medium text-amber-300 hover:bg-amber-400/[0.12] focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {add.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>
      {error ? <div className="mt-1 text-[9px] text-red-300">{error}</div> : null}
    </ProfileSection>
  )
}

// ── Activity ───────────────────────────────────────────────────────────────

export function ActivityGlyph({ kind }: { kind: CrmActivityKind | string }) {
  switch (kind) {
    case 'capture':
      return <Mail size={11} className="text-blue-300" />
    case 'hygiene':
      return <Wand2 size={11} className="text-stone-300" />
    case 'dedupe':
      return <Combine size={11} className="text-amber-300" />
    case 'merge':
      return <GitMerge size={11} className="text-amber-300" />
    case 'unmerge':
      return <RotateCcw size={11} className="text-amber-300" />
    case 'enrich':
      return <Sparkles size={11} className="text-emerald-300" />
    case 'edit':
      return <FileEdit size={11} className="text-sky-300" />
    case 'create':
      return <UserPlus size={11} className="text-emerald-300" />
    case 'retire':
      return <UserMinus size={11} className="text-red-300" />
    case 'restore':
      return <RotateCcw size={11} className="text-emerald-300" />
    case 'list':
      return <ListChecks size={11} className="text-stone-300" />
    case 'note':
      return <MessageSquare size={11} className="text-emerald-300" />
    default:
      return <ArrowRightLeft size={11} className="text-stone-400" />
  }
}

/** One entry of the chain, in the dossier's activity-trail markup. */
export function ActivityEntry({ entry, onOpenContact }: { entry: CrmActivity; onOpenContact?: (id: string) => void }) {
  return (
    <li className="relative pl-6">
      <span className="absolute left-[7px] top-3 h-2.5 w-2.5 rounded-full border border-amber-400/60 bg-[#0a0a0a]" />
      <div className="rounded border border-stone-800/80 bg-black/30 px-3 py-2">
        <div className="flex gap-2.5">
          <div className="mt-0.5 shrink-0">
            <ActivityGlyph kind={entry.kind} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 break-words text-[11px] text-stone-100">{entry.summary}</span>
              <span className="shrink-0 font-mono text-[9px] text-stone-500" title={formatStamp(entry.createdAt)}>
                {relativeTime(entry.createdAt)}
              </span>
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[9px] text-stone-500">
              <span className="font-mono uppercase tracking-widest text-stone-600">{entry.kind}</span>
              {entry.contactId && onOpenContact ? (
                <button
                  type="button"
                  onClick={() => onOpenContact(entry.contactId!)}
                  className="rounded text-amber-300/80 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400/60"
                >
                  {entry.contactName ?? entry.contactId}
                </button>
              ) : entry.contactName ? (
                <span className="text-stone-400">{entry.contactName}</span>
              ) : null}
              <span>
                by{' '}
                <span className={entry.actor ? 'text-stone-400' : 'italic text-stone-600'}>{entry.actor ?? 'not recorded'}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </li>
  )
}

export function ActivitySection({ contactId, limit = 8 }: { contactId: string; limit?: number }) {
  const activity = useQuery({
    queryKey: ['crm', 'activity', 'contact', contactId, limit],
    queryFn: () => crmService.getContactActivity(contactId, limit),
  })
  return (
    <ProfileSection title="Activity" icon={<Activity size={11} className="text-amber-400" />}>
      {activity.isPending ? (
        <div className="flex items-center gap-1.5 text-[10px] text-stone-500">
          <Loader2 size={10} className="motion-safe:animate-spin" /> Reading the trail…
        </div>
      ) : activity.isError ? (
        <div className="text-[10px] text-red-300">{crmErrorDetail(activity.error, 'The activity trail could not be read.')}</div>
      ) : activity.data.items.length === 0 ? (
        <div className="text-[11px] italic text-stone-500">Nothing recorded for this contact yet.</div>
      ) : (
        <ol className="space-y-1.5 border-l border-amber-400/25">
          {activity.data.items.map((entry) => (
            <ActivityEntry key={entry.id} entry={entry} />
          ))}
        </ol>
      )}
    </ProfileSection>
  )
}
